// frontend/src/pages/admin/AdminAiUsagePage.tsx

import { useEffect, useState } from 'react';
import { adminService } from '../../services/admin.service';
import { AiUsageSummary, FEATURE_LABEL } from '../../types/admin.types';
import { Bot, Coins, PhoneCall, FileDown, FileUp } from 'lucide-react';

export function AdminAiUsagePage() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<AiUsageSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    adminService
      .getAiUsage(days)
      .then(setSummary)
      .catch((err) => setError(err instanceof Error ? err.message : 'Veri alınamadı.'))
      .finally(() => setIsLoading(false));
  }, [days]);

  if (isLoading) return <div className="courses-loading">Yükleniyor...</div>;
  if (error) return <div className="courses-error">{error}</div>;
  if (!summary) return null;

  const maxDailyCost = Math.max(...summary.byDay.map((d) => d.costUsd), 0.001);

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          AI Kullanımı <Bot size={28} color="#00AEEF" />
        </h1>
        <p>Gemini maliyet ve kullanım istatistikleri (tahmini, gerçek token sayılarına dayalı)</p>
      </header>

      <div className="admin-filters" style={{ marginBottom: 24 }}>
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            type="button"
            className={`courses-tab ${days === d ? 'active' : ''}`}
            onClick={() => setDays(d)}
          >
            Son {d} Gün
          </button>
        ))}
      </div>

      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Coins size={22} color="#00AEEF" />
          </div>
          <div className="admin-stat-value">${summary.totalCostUsd.toFixed(2)}</div>
          <div className="admin-stat-label">Tahmini Toplam Maliyet</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PhoneCall size={22} color="#10C27A" />
          </div>
          <div className="admin-stat-value">{summary.totalCalls.toLocaleString('tr-TR')}</div>
          <div className="admin-stat-label">Toplam API Çağrısı</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FileDown size={22} color="#7C3AED" />
          </div>
          <div className="admin-stat-value">{(summary.totalInputTokens / 1000).toFixed(0)}K</div>
          <div className="admin-stat-label">Girdi Token</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FileUp size={22} color="#F59E0B" />
          </div>
          <div className="admin-stat-value">{(summary.totalOutputTokens / 1000).toFixed(0)}K</div>
          <div className="admin-stat-label">Çıktı Token</div>
        </div>
      </div>

      <div className="grid grid-2" style={{ marginTop: 32 }}>
        <div className="card">
          <h3 className="parent-section-title">Özellik Bazlı Dağılım</h3>
          {summary.byFeature.length === 0 ? (
            <p className="parent-empty-text">Henüz veri yok.</p>
          ) : (
            <div className="admin-feature-list">
              {summary.byFeature.map((f) => (
                <div key={f.feature} className="admin-feature-row">
                  <span>{FEATURE_LABEL[f.feature] ?? f.feature}</span>
                  <span className="admin-feature-stats">
                    {f.calls} çağrı • ${f.costUsd.toFixed(3)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <h3 className="parent-section-title">En Çok Sorulan Konular</h3>
          {summary.topTopics.length === 0 ? (
            <p className="parent-empty-text">Henüz veri yok.</p>
          ) : (
            <div className="admin-feature-list">
              {summary.topTopics.map((t) => (
                <div key={t.topic} className="admin-feature-row">
                  <span>{t.topic}</span>
                  <span className="admin-feature-stats">{t.count} kez</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <h3 className="parent-section-title">Günlük Maliyet Trendi</h3>
        {summary.byDay.length === 0 ? (
          <p className="parent-empty-text">Henüz veri yok.</p>
        ) : (
          <div className="admin-bar-chart">
            {summary.byDay.map((d) => (
              <div key={d.date} className="admin-bar-chart-col" title={`${d.date}: $${d.costUsd.toFixed(3)}`}>
                <div
                  className="admin-bar-chart-bar"
                  style={{ height: `${Math.max(4, (d.costUsd / maxDailyCost) * 100)}%` }}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <h3 className="parent-section-title">Kullanıcı Başına Maliyet (İlk 10)</h3>
        {summary.costPerStudent.length === 0 ? (
          <p className="parent-empty-text">Henüz veri yok.</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Öğrenci</th>
                  <th>Çağrı Sayısı</th>
                  <th>Tahmini Maliyet</th>
                </tr>
              </thead>
              <tbody>
                {summary.costPerStudent.map((s) => (
                  <tr key={s.studentId}>
                    <td>{s.studentName}</td>
                    <td>{s.calls}</td>
                    <td>${s.costUsd.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
